/**
 * GROUPE ROPS — Page d'enrôlement 2FA TOTP (CEO/DAF).
 * URL publique : /2fa-setup
 *
 * Pas d'auth gate (le user doit déjà connaître son username pour s'enrôler).
 * Le flow :
 *  1. Saisir son username
 *  2. Cliquer "Activer 2FA" → reçoit QR code + secret
 *  3. Scanner avec Google Authenticator
 *  4. Saisir le code 6 chiffres → 2FA activée + 8 backup codes affichés
 *  5. Imprimer/recopier les backup codes (ils ne seront jamais ré-affichés)
 *
 * Permet aussi de désactiver le 2FA en saisissant un code valide.
 */
import React, { useState } from 'react';
import { ShieldCheck, ShieldOff, QrCode, Copy, Printer, AlertTriangle, CheckCircle2, KeyRound, Smartphone } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STEPS = {
  IDLE: 'idle',
  ENROLLING: 'enrolling',
  CONFIRMING: 'confirming',
  ENABLED: 'enabled',
  BACKUP_DISPLAYED: 'backup_displayed',
  DISABLING: 'disabling',
};

async function api(path, opts = {}) {
  const r = await fetch(`${BACKEND}/api/legacy/2fa${path}`, {
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-store',
    ...opts,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const msg = data.detail || data.message || `HTTP ${r.status}`;
    throw new Error(msg);
  }
  return data;
}

export default function TwoFactorSetup() {
  const [username, setUsername] = useState('');
  const [step, setStep] = useState(STEPS.IDLE);
  const [statusInfo, setStatusInfo] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [manualKey, setManualKey] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Lookup status when username changes (debounced via blur)
  const refreshStatus = async (u) => {
    if (!u || u.length < 2) { setStatusInfo(null); return; }
    try {
      const data = await api(`/status?username=${encodeURIComponent(u.trim().toLowerCase())}`);
      setStatusInfo(data);
    } catch (_) { setStatusInfo(null); }
  };

  // (effect supprimé : reset des erreurs fait imperatively lors des actions)

  const startEnroll = async () => {
    if (!username.trim()) { setError('Saisis ton nom d\'utilisateur'); return; }
    setBusy(true); setError(null);
    try {
      const data = await api('/setup-start', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim().toLowerCase() }),
      });
      setQrData(data.qr_code_data_uri);
      setManualKey(data.manual_key);
      setStep(STEPS.ENROLLING);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    if (!code.trim()) { setError('Saisis le code 6 chiffres'); return; }
    setBusy(true); setError(null);
    try {
      const data = await api('/setup-confirm', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim().toLowerCase(), code: code.trim() }),
      });
      if (data.success) {
        setBackupCodes(data.backup_codes);
        setStep(STEPS.BACKUP_DISPLAYED);
        setCode('');
        await refreshStatus(username);
      } else {
        setError(data.message || 'Code invalide');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const disable2FA = async () => {
    if (!code.trim()) { setError('Saisis un code TOTP ou backup pour confirmer'); return; }
    setBusy(true); setError(null);
    try {
      const data = await api('/disable', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim().toLowerCase(), code: code.trim() }),
      });
      if (data.success) {
        setStep(STEPS.IDLE);
        setCode('');
        setBackupCodes(null);
        setQrData(null);
        setManualKey(null);
        await refreshStatus(username);
      } else {
        setError(data.message || 'Code invalide');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyManualKey = () => {
    if (!manualKey) return;
    navigator.clipboard.writeText(manualKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const printBackupCodes = () => {
    if (!backupCodes) return;
    const win = window.open('', '_blank', 'width=600,height=700');
    win.document.write(`
      <html><head><title>GROUPE ROPS - Codes de récupération 2FA - ${username}</title>
      <style>
        body{font-family:monospace;padding:40px;color:#0a1628;line-height:1.6}
        h1{color:#0a1628;letter-spacing:-0.5px;margin-bottom:4px}
        .sub{color:#64748b;font-size:13px;margin-bottom:32px}
        .codes{display:grid;grid-template-columns:1fr 1fr;gap:14px;font-size:18px;font-weight:bold;margin:24px 0}
        .code{padding:14px 20px;border:2px solid #0a1628;border-radius:8px;text-align:center;letter-spacing:3px}
        .warn{margin-top:32px;padding:18px;background:#fef3c7;border:2px solid #f59e0b;border-radius:8px;font-weight:600}
      </style>
      </head><body>
        <h1>GROUPE ROPS — Codes de récupération 2FA</h1>
        <div class="sub">Utilisateur : <strong>${username}</strong> · Généré le ${new Date().toLocaleString('fr-FR')}</div>
        <div class="codes">
          ${backupCodes.map(c => `<div class="code">${c}</div>`).join('')}
        </div>
        <div class="warn">
          ⚠️ Garde cette feuille en lieu sûr.<br>
          Chaque code n'est utilisable QU'UNE SEULE FOIS.<br>
          Si tu perds ton téléphone, ces codes te permettent de te reconnecter.
        </div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="totp-shell" data-testid="totp-setup-page">
      <div className="totp-bg"></div>

      <div className="totp-card">
        <header className="totp-header">
          <div className="totp-logo">
            <ShieldCheck size={28} strokeWidth={2.4} />
          </div>
          <div>
            <h1>Authentification à deux facteurs</h1>
            <p>Sécurise ton compte avec Google Authenticator, Authy ou équivalent</p>
          </div>
        </header>

        {/* Étape 1 : Username + status */}
        <section className="totp-section">
          <label className="totp-label">Nom d&apos;utilisateur</label>
          <input
            type="text"
            className="totp-input"
            placeholder="Ex: ratanga.archil"
            value={username}
            onChange={(e) => { setUsername(e.target.value); }}
            onBlur={() => refreshStatus(username)}
            disabled={step !== STEPS.IDLE && step !== STEPS.DISABLING}
            data-testid="totp-username-input"
            autoComplete="off"
          />

          {statusInfo && (
            <div className={`totp-status totp-status-${statusInfo.enabled ? 'on' : 'off'}`}>
              {statusInfo.enabled ? (
                <>
                  <CheckCircle2 size={18} />
                  <span>{`2FA active pour ce compte · ${statusInfo.backup_codes_remaining} code${statusInfo.backup_codes_remaining > 1 ? 's' : ''} de récupération restant${statusInfo.backup_codes_remaining > 1 ? 's' : ''}`}</span>
                </>
              ) : statusInfo.pending ? (
                <>
                  <AlertTriangle size={18} />
                  <span>{'Setup 2FA en cours — confirme le code pour activer'}</span>
                </>
              ) : (
                <>
                  <ShieldOff size={18} />
                  <span>{'2FA désactivée pour ce compte'}</span>
                </>
              )}
            </div>
          )}
        </section>

        {/* Action principale */}
        {step === STEPS.IDLE && statusInfo && !statusInfo.enabled && (
          <button
            className="totp-btn-primary"
            onClick={startEnroll}
            disabled={busy || !username.trim()}
            data-testid="totp-enroll-btn"
          >
            <QrCode size={18} />
            {busy ? 'Génération…' : 'Activer le 2FA pour ce compte'}
          </button>
        )}

        {step === STEPS.IDLE && statusInfo && statusInfo.enabled && (
          <button
            className="totp-btn-danger"
            onClick={() => setStep(STEPS.DISABLING)}
            disabled={busy}
            data-testid="totp-disable-btn"
          >
            <ShieldOff size={18} />
            Désactiver le 2FA
          </button>
        )}

        {/* Étape 2 : QR code visible + confirmer code */}
        {step === STEPS.ENROLLING && qrData && (
          <section className="totp-section totp-enroll">
            <div className="totp-step">
              <Smartphone size={18} />
              <span>1. Scanne ce QR code avec ton app authenticator</span>
            </div>
            <div className="totp-qr-wrap">
              <img src={qrData} alt="QR Code 2FA" className="totp-qr" data-testid="totp-qr-image" />
            </div>
            <details className="totp-details">
              <summary>Impossible de scanner ? Saisie manuelle</summary>
              <div className="totp-manual">
                <code data-testid="totp-manual-key">{manualKey}</code>
                <button onClick={copyManualKey} className="totp-btn-icon" data-testid="totp-copy-key">
                  <Copy size={14} /> {copied ? 'Copié !' : 'Copier'}
                </button>
              </div>
              <small>Issuer : <strong>GROUPE ROPS</strong> · 6 chiffres · 30 secondes</small>
            </details>
            <div className="totp-step">
              <KeyRound size={18} />
              <span>2. Saisis le code 6 chiffres affiché dans l&apos;app</span>
            </div>
            <input
              type="text"
              className="totp-input totp-input-code"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              autoFocus
              data-testid="totp-code-input"
              inputMode="numeric"
            />
            <button
              className="totp-btn-primary"
              onClick={confirmEnroll}
              disabled={busy || code.length !== 6}
              data-testid="totp-confirm-btn"
            >
              {busy ? 'Vérification…' : 'Confirmer & activer'}
            </button>
          </section>
        )}

        {/* Étape 3 : Backup codes affichés */}
        {step === STEPS.BACKUP_DISPLAYED && backupCodes && (
          <section className="totp-section totp-backup" data-testid="totp-backup-codes">
            <div className="totp-success">
              <CheckCircle2 size={24} />
              <div>
                <strong>2FA activée !</strong>
                <p>À partir de maintenant, tu devras saisir un code à chaque connexion.</p>
              </div>
            </div>
            <div className="totp-warn">
              <AlertTriangle size={18} />
              <span><strong>Imprime ou recopie ces 8 codes de récupération.</strong> Ils ne seront <em>jamais</em> ré-affichés.</span>
            </div>
            <div className="totp-codes-grid">
              {backupCodes.map((c, i) => (
                <div key={i} className="totp-code-cell" data-testid={`totp-backup-${i}`}>{c}</div>
              ))}
            </div>
            <div className="totp-actions">
              <button onClick={printBackupCodes} className="totp-btn-secondary" data-testid="totp-print-btn">
                <Printer size={16} /> Imprimer
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(backupCodes.join('\n'));
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="totp-btn-secondary"
              >
                <Copy size={16} /> {copied ? 'Copié !' : 'Copier la liste'}
              </button>
              <button
                onClick={() => { setStep(STEPS.IDLE); refreshStatus(username); }}
                className="totp-btn-primary"
                data-testid="totp-done-btn"
              >
                J&apos;ai sauvegardé mes codes
              </button>
            </div>
          </section>
        )}

        {/* Étape 4 : Désactivation */}
        {step === STEPS.DISABLING && (
          <section className="totp-section">
            <div className="totp-warn">
              <AlertTriangle size={18} />
              <span>Saisis ton code TOTP actuel <strong>ou</strong> un code de récupération pour confirmer.</span>
            </div>
            <input
              type="text"
              className="totp-input totp-input-code"
              placeholder="Code TOTP ou backup"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              maxLength={8}
              autoFocus
              data-testid="totp-disable-code-input"
              inputMode="numeric"
            />
            <div className="totp-actions">
              <button onClick={() => { setStep(STEPS.IDLE); setCode(''); }} className="totp-btn-secondary">
                Annuler
              </button>
              <button
                onClick={disable2FA}
                className="totp-btn-danger"
                disabled={busy || code.length < 6}
                data-testid="totp-confirm-disable-btn"
              >
                {busy ? 'Désactivation…' : 'Désactiver le 2FA'}
              </button>
            </div>
          </section>
        )}

        {error && (
          <div className="totp-error" data-testid="totp-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <footer className="totp-footer">
          <a href="/legacy-v2.html" data-testid="totp-back-legacy">← Retour à l&apos;app legacy</a>
          <a href="/status" target="_blank" rel="noopener noreferrer" data-testid="totp-status-link">
            <span className="totp-dot"></span> État plateforme
          </a>
        </footer>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&family=Inter:wght@400;500;600;700;800&display=swap');

        .totp-shell {
          min-height: 100vh;
          background: #0a1628;
          color: #e5e7eb;
          font-family: 'Inter', system-ui, sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          position: relative;
          overflow: hidden;
        }
        .totp-bg {
          position: absolute; inset: 0;
          background-image:
            radial-gradient(circle at 20% 10%, rgba(214,243,47,0.05) 0%, transparent 60%),
            radial-gradient(circle at 80% 90%, rgba(56,189,248,0.04) 0%, transparent 60%);
          pointer-events: none;
        }
        .totp-card {
          width: 100%;
          max-width: 540px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 40px;
          backdrop-filter: blur(20px);
          position: relative;
          z-index: 1;
          box-shadow: 0 20px 60px rgba(0,0,0,0.4);
        }
        .totp-header {
          display: flex; align-items: center; gap: 16px;
          margin-bottom: 32px;
        }
        .totp-logo {
          width: 56px; height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #D6F32F, #84cc16);
          color: #0a1628;
          display: grid; place-items: center;
          box-shadow: 0 8px 24px rgba(214,243,47,0.3);
        }
        .totp-header h1 {
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.5px;
          color: #fff;
          margin: 0;
        }
        .totp-header p {
          font-size: 13px;
          color: #a1a1aa;
          margin: 4px 0 0;
        }
        .totp-section {
          margin-bottom: 20px;
        }
        .totp-label {
          display: block;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #71717a;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .totp-input {
          width: 100%;
          padding: 14px 16px;
          background: rgba(0,0,0,0.3);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: #fff;
          font: 500 14px Inter;
          transition: all .2s;
        }
        .totp-input:focus {
          outline: none;
          border-color: #D6F32F;
          background: rgba(0,0,0,0.4);
          box-shadow: 0 0 0 3px rgba(214,243,47,0.15);
        }
        .totp-input-code {
          font: 700 24px 'JetBrains Mono', monospace;
          text-align: center;
          letter-spacing: 8px;
        }
        .totp-status {
          margin-top: 12px;
          padding: 12px 16px;
          border-radius: 10px;
          display: flex; align-items: center; gap: 10px;
          font-size: 13px;
        }
        .totp-status-on  { background: rgba(34,197,94,0.1);  color: #86efac; border: 1px solid rgba(34,197,94,0.3); }
        .totp-status-off { background: rgba(245,158,11,0.08); color: #fcd34d; border: 1px solid rgba(245,158,11,0.2); }

        .totp-step {
          display: flex; align-items: center; gap: 10px;
          margin: 16px 0 10px;
          font-size: 13px;
          color: #D6F32F;
          font-weight: 600;
        }
        .totp-qr-wrap {
          display: flex; justify-content: center;
          padding: 16px;
          background: #fff;
          border-radius: 14px;
          margin: 8px 0;
        }
        .totp-qr {
          max-width: 220px; width: 100%;
          image-rendering: pixelated;
        }
        .totp-details {
          margin: 8px 0 16px;
          font-size: 12px;
          color: #a1a1aa;
        }
        .totp-details summary {
          cursor: pointer;
          padding: 8px 0;
          color: #71717a;
          transition: color .2s;
        }
        .totp-details summary:hover { color: #D6F32F; }
        .totp-manual {
          display: flex; align-items: center; gap: 10px;
          margin-top: 10px;
          padding: 12px;
          background: rgba(0,0,0,0.3);
          border-radius: 8px;
        }
        .totp-manual code {
          flex: 1;
          font-family: 'JetBrains Mono', monospace;
          color: #D6F32F;
          word-break: break-all;
          font-size: 13px;
        }
        .totp-btn-icon {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 6px 10px;
          background: rgba(214,243,47,0.1);
          border: 1px solid rgba(214,243,47,0.3);
          color: #D6F32F;
          border-radius: 8px;
          font: 600 11px Inter;
          cursor: pointer;
          transition: all .2s;
        }
        .totp-btn-icon:hover { background: rgba(214,243,47,0.2); }

        .totp-btn-primary, .totp-btn-secondary, .totp-btn-danger {
          width: 100%;
          padding: 14px 20px;
          border: 0;
          border-radius: 12px;
          font: 700 14px Inter;
          letter-spacing: 0.2px;
          cursor: pointer;
          transition: all .2s;
          display: inline-flex; align-items: center; justify-content: center;
          gap: 10px;
          margin-top: 8px;
        }
        .totp-btn-primary {
          background: linear-gradient(135deg, #D6F32F, #84cc16);
          color: #0a1628;
        }
        .totp-btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(214,243,47,0.3);
        }
        .totp-btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
        .totp-btn-secondary {
          background: rgba(255,255,255,0.06);
          color: #e5e7eb;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .totp-btn-secondary:hover { background: rgba(255,255,255,0.1); }
        .totp-btn-danger {
          background: rgba(239,68,68,0.15);
          color: #fca5a5;
          border: 1px solid rgba(239,68,68,0.4);
        }
        .totp-btn-danger:hover { background: rgba(239,68,68,0.25); }
        .totp-btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }

        .totp-success {
          display: flex; gap: 14px;
          padding: 18px;
          background: rgba(34,197,94,0.1);
          border: 1px solid rgba(34,197,94,0.3);
          border-radius: 12px;
          margin-bottom: 16px;
          color: #86efac;
        }
        .totp-success strong { color: #fff; display: block; font-size: 15px; }
        .totp-success p { margin: 4px 0 0; font-size: 12px; color: #d1d5db; }

        .totp-warn {
          display: flex; gap: 10px;
          padding: 14px;
          background: rgba(245,158,11,0.1);
          border: 1px solid rgba(245,158,11,0.3);
          border-radius: 10px;
          margin: 14px 0;
          color: #fcd34d;
          font-size: 13px;
          line-height: 1.5;
        }
        .totp-codes-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin: 18px 0;
        }
        .totp-code-cell {
          padding: 14px;
          background: rgba(0,0,0,0.3);
          border: 1px solid rgba(214,243,47,0.2);
          border-radius: 8px;
          font: 700 18px 'JetBrains Mono', monospace;
          color: #D6F32F;
          text-align: center;
          letter-spacing: 2px;
        }
        .totp-actions {
          display: flex; gap: 10px;
          flex-wrap: wrap;
        }
        .totp-actions > button { flex: 1; min-width: 140px; }

        .totp-error {
          display: flex; align-items: center; gap: 8px;
          padding: 12px 14px;
          background: rgba(239,68,68,0.1);
          border: 1px solid rgba(239,68,68,0.3);
          border-radius: 10px;
          color: #fca5a5;
          font-size: 13px;
          margin-top: 14px;
        }
        .totp-footer {
          display: flex; justify-content: space-between;
          margin-top: 28px;
          padding-top: 20px;
          border-top: 1px solid rgba(255,255,255,0.06);
          font-size: 12px;
        }
        .totp-footer a {
          color: #71717a;
          text-decoration: none;
          display: inline-flex; align-items: center; gap: 6px;
          transition: color .2s;
        }
        .totp-footer a:hover { color: #D6F32F; }
        .totp-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #22c55e;
          box-shadow: 0 0 5px rgba(34,197,94,0.6);
          display: inline-block;
        }

        @media (max-width: 480px) {
          .totp-card { padding: 24px; }
          .totp-codes-grid { grid-template-columns: 1fr; }
          .totp-header h1 { font-size: 18px; }
        }
      `}</style>
    </div>
  );
}
